import React, { useState, useRef, useEffect } from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Button,
    IconButton,
    Card,
    styled,
    FormHelperText,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    ListItemButton,
    List,
    Typography,
    Tooltip,
    ButtonGroup
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import dayjs from "dayjs";

import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

import { useFormik } from 'formik';

import { useAppContextFeatures } from "../../../context/AppContext";
import { useAuthAccess } from "../../../hooks/useAuthAccess";

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from "../../common/CommonRestricted";
import DatePickerComponent from "../../common/DatePickerComponent";

import SearchItems from "../../dialog/SearchItems";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";

import { CommonIcons, icons } from "../../common/CommonIcons";
import { useDocuments, documentTypes, targetTypes } from "../../../hooks/api/useDocuments";
import { useTranslations } from '../../../hooks/useTranslations';

import { validationSchema, initialValues, initialRowValues, adjustmentTypes } from "../../../schema/documents/itemAdjustmentSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

export default function UpdateItemAdjustment({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();

    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const { getAvailableLocations } = useAuthAccess();
    const { getLocationIds, setLocationIds } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.itemAdjustment.id)

    const { setSystemLoading } = useAppContextFeatures();
    const { isLoading, get, update, filterOperatorTypes } = useDocuments({ 
        documentType: documentTypes.itemAdjustment, 
        targetType: targetTypes.itemAdjustment
    })
    const [savedValues, setSavedValues] = useState()

    const availableLocations = getAvailableLocations();

    const [searchDocumentConfig, setSearchDocumentConfig] = useState({
        show: false,
        documentType: documentTypes.itemAdjustment,
        keyword: ""
    })

    const loadData = async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id })
            const { document, items } = data;

            // create rows
            const rows = items.map((i) => {
                return {
                    docItemId: i?.id,
                    itemId: i?.item_id,
                    name: i?.item_name,
                    code: i?.code,
                    description: i?.description,
                    qty: parseFloat(i?.qty),
                    uomName: i?.uom_name,
                    uomId: i?.uom_id,
                    price: i?.uom_price,
                    scanType: i?.scan_type === "1",
                    adjustmentType: i?.adjustment_type
                }
            })

            setSavedValues({
                locationId: document?.location_id,
                locationName: document?.location_name,
                documentNumber: document?.doc_number,
                documentDate: dayjs(document?.doc_date),
                remark: document?.doc_remark ? document.doc_remark : "",
                rows,
            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }

    const onSubmit = async (currentValues) => {

        const document = {
            doc_date: currentValues.documentDate.format("YYYY-MM-DD"),
            doc_remark: currentValues.remark,
        }

        const props = {
            location_id: currentValues.locationId,

        }

        let items = [];

        currentValues.rows.map(item => {
            items.push({
                id: item?.itemId,
                doc_item_id: item?.docItemId,
                code: item?.code,
                name: item?.name,
                description: item?.description,
                qty: item?.qty,
                uom_lbl: item?.uomName,
                uom: item?.uomId,
                price: item?.price,
                props: {
                    scan_type: item?.scanType ? 1 : 0,
                    adjustment_type: item?.adjustmentType

                }
            })
            return false;
        })

        try {

            let data = {
                document,
                props,
                items
            }

            const response = await update({ id, data });

            promptMessage(true, `${response?.message}`)

            navigate(getRoute(`documents/itemAdjustment/view/${id}`))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { handleSubmit, handleChange, values, errors, touched, setFieldValue, } = formik;

    // useEffect(() => {

    //     const searchParamsAction = searchParams.get('action');
    //     const searchParamsPayload = searchParams.get('payload');

    //     // console.log('searchParams', searchParamsAction, searchParamsPayload)

    //     if(searchParamsAction && searchParamsAction.toLowerCase() === 'duplicate'){
    //         setSearchDocumentConfig(prev => ({ ...prev, show: true, documentType: documentTypes.itemAdjustment, keyword: searchParamsPayload}))
    //     }

    // }, [])

    useEffect(() => {

        loadData(id)

    }, [])

    // useEffect(() => {

    //     const currentLocationIds = getLocationIds();

    //     // setSelectLocationIds(currentLocationIds)

    //     if(currentLocationIds.length > 0){
    //         // console.log('currentLocationIds', currentLocationIds, availableLocations)

    //         // get the location information
    //         const loc = availableLocations.filter(item => item.id === currentLocationIds[0])

    //         if(loc.length){
    //             setFieldValue("locationId", loc[0].id)
    //             setFieldValue("locationName", loc[0].location_name)
    //         }
    //     }

    // }, [getLocationIds])

    const validateLocationSelection = () => {
        // return (selectLocationIds && selectLocationIds.length === 1)
        const selected = getLocationIds()
        return selected && selected.length === 1
    }

    const handleAddItem = () => {

        let currentRows = values?.rows;

        currentRows.push(initialRowValues);

        setFieldValue('rows', currentRows)

    }

    const handleRemoveItem = ({ rows, index, setFieldValue }) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, t(`itemsAtLeastHave1Row`))
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        // rows.remove(index);
        setFieldValue('rows', newRows)

        return true;

    }

    const RenderRows = () => {

        const rows = values?.rows;

        return (
            <Box
            >
                <Box
                >
                    {
                        rows && rows.length > 0
                        && rows.map((item, index) => {

                            const itemValues = item;
                            let itemTouched = touched?.rows
                            let itemErrors = errors?.rows
                            // const itemTouched = touched?.rows

                            if (itemTouched) {
                                itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
                            }

                            if (itemErrors) {
                                itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
                            }

                            return (
                                <Grid key={index} item xs={12} sm={12} md={12}>

                                    <Grid container spacing={0.5} >

                                        <Grid item xs={12} sm={12} md={0.5} alignSelf={"center"}>
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => handleRemoveItem({ rows: values.rows, index, setFieldValue })}
                                            >
                                                <CommonIcons name={icons.remove} />
                                            </IconButton>
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={2.5}>

                                            <SearchItems
                                                id={`rows.${index}.name`}
                                                handleOnCancel={() => { }}
                                                handleOnSearchChange={(event) => {
                                                    // setFieldValue(`rows.${index}.name`, `${event.target.value}`)
                                                }}
                                                handleOnSelect={({ id, item }) => {

                                                    setFieldValue(`rows.${index}.itemId`, item?.id)
                                                    setFieldValue(`rows.${index}.docItemId`, '')
                                                    setFieldValue(`rows.${index}.name`, item?.item_name)
                                                    setFieldValue(`rows.${index}.description`, item?.description)
                                                    setFieldValue(`rows.${index}.code`, item?.code)
                                                    setFieldValue(`rows.${index}.uomName`, item?.uom_name)
                                                    setFieldValue(`rows.${index}.uomId`, item?.uom_id)
                                                    setFieldValue(`rows.${index}.qty`, 1)
                                                    setFieldValue(`rows.${index}.scanType`, item?.scan_type === "1")
                                                    setFieldValue(`rows.${index}.price`, item?.uom_price)
                                                    setFieldValue(`rows.${index}.adjustmentType`, item?.adjustment_type === "")
                                                }}
                                                textProps={{
                                                    disabled: true,
                                                    value: itemValues?.name ? itemValues.name : "",
                                                    error: Boolean(itemErrors?.name),
                                                    helperText: itemErrors?.name
                                                }}
                                            />

                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1.5}>
                                            <TextField
                                                disabled
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.code`}
                                                label={t("code")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.code ? itemValues.code : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.code)}
                                                helperText={itemErrors?.code}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={4}>
                                            <TextField
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.description`}
                                                label={t("description")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.description ? itemValues.description : ""}
                                                onChange={handleChange}
                                                error={Boolean(itemErrors?.description)}
                                                helperText={itemErrors?.description}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1}>
                                            <TextField
                                                inputProps={{
                                                    style: {
                                                        textAlign: "center"
                                                    }
                                                }}
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.qty`}
                                                label={
                                                    `${t('qty')} ${itemValues?.uomName ? "(".concat(itemValues.uomName, ")") : ""}`
                                                }
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.qty ? itemValues.qty : ""}
                                                onChange={(event) => {
                                                    setFieldValue(`rows.${index}.qty`, event.target.value);
                                                    // handleDisplayRowChange({ index, qty: event.target.value});
                                                }}
                                                error={Boolean(itemErrors?.qty)}
                                                helperText={itemErrors?.qty}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1.5}>
                                            <TextField
                                                disabled
                                                margin="dense"
                                                size="small"
                                                fullWidth
                                                name={`rows.${index}.price`}
                                                label={t("price")}
                                                type="text"
                                                variant="outlined"
                                                value={itemValues?.price ? itemValues.price : ""}
                                            // onChange={handleChange}
                                            // error={Boolean(itemErrors?.price)}
                                            // helperText={itemErrors?.price}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={12} md={1}>
                                            <ButtonGroup
                                                fullWidth
                                                sx={{
                                                    my: 1
                                                }}
                                            >
                                                <Tooltip
                                                    title={t("increase")}
                                                >
                                                    <Button
                                                        variant={
                                                            itemValues.adjustmentType === adjustmentTypes.increase.id ? "contained" : "outlined"
                                                        }
                                                        onClick={() => {
                                                            setFieldValue(`rows.${index}.adjustmentType`, adjustmentTypes.increase.id)
                                                        }}
                                                    >+</Button>
                                                </Tooltip>

                                                <Tooltip
                                                    title={t("decrease")}

                                                >
                                                    <Button
                                                        variant={
                                                            itemValues.adjustmentType === adjustmentTypes.decrease.id ? "contained" : "outlined"
                                                        }
                                                        onClick={() => {
                                                            setFieldValue(`rows.${index}.adjustmentType`, adjustmentTypes.decrease.id)
                                                        }}
                                                    >-</Button>
                                                </Tooltip>
                                            </ButtonGroup>
                                        </Grid>

                                    </Grid>
                                </Grid>
                            )
                        })
                    }
                </Box>

                <Grid item xs={12}>
                    <Divider sx={{ mt: 1, mb: 0 }} />
                </Grid>

                {
                    RenderSummaryRow()
                }

            </Box>
        )
    }

    const RenderSummaryRow = () => {
        return (
            <Grid item xs={12} sm={12} md={12}>

                <Grid container spacing={0.5}>

                    <Grid item xs={12} sm={12} md={4}>

                        <Button
                            variant="outlined"
                            sx={{ mt: 2 }}
                            fullWidth
                            startIcon={<CommonIcons name={icons.add} />}
                            onClick={() => handleAddItem()}
                        >
                            {t('addItem')}
                        </Button>

                    </Grid>

                    <Grid item xs={12} sm={12} md={5}>
                        <></>
                    </Grid>
                </Grid>

            </Grid>
        )
    }

    const RenderFormLocation = () => {

        // console.log("V", values.locationId)

        let locationsTo = [];

        // get the current available location To 
        if (values.locationId)
            locationsTo = availableLocations.filter(i => i.id !== values?.locationId)

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`location`)}
                            subTitle={t(`relatedWarehouseLocationToProcess`)}
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                        <TextField
                            disabled
                            margin="dense"
                            size="small"
                            fullWidth
                            name={"locationId"}
                            label={t("fromLocation")}
                            type="text"
                            variant="outlined"
                            value={values.locationName}
                            error={touched.locationId && Boolean(errors.locationId)}
                            helperText={touched.locationId && errors.locationId}
                        />
                    </Grid>
                    {/* <Grid item md={1.5}>
                        <IconButton
                            color="primary"
                            sx={{
                                // pt: 0
                            }}
                            onClick={() => {
                                setLocationIds([])
                            }}
                        >
                            <StyledIcon
                                sx={{
                                    width: 25,
                                    height: 25,
                                    m: 0,
                                    p: 0
                                }}
                            >
                                <CommonIcons name={icons.edit} />
                            </StyledIcon>
                        </IconButton>
                    </Grid> */}

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "documentDate",
                                        label: t("documentDate")
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                />

                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label={t("remark")}
                                    type="text"
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    value={values.remark}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (
            <form onSubmit={handleSubmit}>
                {/* {JSON.stringify(errors)} */}
                <Grid container spacing={1}>

                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>


                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                        {
                            RenderFormLocation()
                        }

                    </Grid>

                </Grid>
                <StyledCard sx={{ mt: 1 }}>
                    <Grid container rowSpacing={0}>
                        <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t(`items`)}
                                subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                            {
                                RenderRows()
                            }

                            {/* <FieldArray
                                    name="rows"
                                    render={(arrayHelpers) => {
                                        
                                        return RenderRow({ values, errors, touched, handleChange, setFieldValue, arrayHelpers})
                                    
                                    }}
                                /> */}
                        </Grid>

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>

            </form>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateItemAdjustment })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("updateItemAdjustment")}
                // actionMenus={[
                //     {
                //         displayText: "Load From ...",
                //         onClick: () => {

                //             // check the location is selected - valid
                //             if(validateLocationSelection() === false){
                //                 promptMessage(false, `Please select any location`)
                //                 return false;
                //             }

                //             // setOpenSearchDocuments(true)
                //             setSearchDocumentConfig(prev => ({...prev, show: true}))

                //             return true;
                //         }
                //     }
                // ]}
                breadcrumbs={[
                    {
                        displayText: t("itemAdjustments"),
                        props: {
                            href: getRoute('documents/itemAdjustments')
                        }
                    },
                    {
                        displayText: t("updateItemAdjustment"),
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}